import { Injectable } from '@angular/core';
import {
  ApiCallHandler,
  EoHttpClient,
  IDataService,
  IHttpParams,
  PaginatedSearchResult,
} from 'ngx-eo-web-components';
import { CoreService } from '../../modules/core/core.service';
import { Observable } from 'rxjs';
import { ISortParams } from '../../modules/shared/models/sort.model';
import { HospitalAvailability } from '../../modules/core/models/hospital-availability.model';
import { PrivateHospitalAvailability } from '../../modules/core/models/private-hospital-availability.model';

@Injectable({
  providedIn: 'root',
})
export class HospitalAvailabilityService
  implements IDataService<HospitalAvailability>
{
  constructor(private http: EoHttpClient, private coreService: CoreService) {}
  public searchCh =
    new ApiCallHandler<PaginatedSearchResult<HospitalAvailability> | null>();

  public hospitalAvailabilityListCallHandler = new ApiCallHandler<
    HospitalAvailability[]
  >();
  public hospitalAvailabilityListByHospitalCallHandler = new ApiCallHandler<
    HospitalAvailability[]
  >();

  public hospitalAvailabilityFetchCallHandler =
    new ApiCallHandler<HospitalAvailability>();
  public hospitalAvailabilityPrivateFetchCallHandler =
    new ApiCallHandler<PrivateHospitalAvailability[]>();

  public hospitalAvailabilitySaveCallHandler =
    new ApiCallHandler<HospitalAvailability | null>();
  public hospitalAvailabilityUpdateCallHandler =
    new ApiCallHandler<HospitalAvailability | null>();
  public hospitalAvailabilityDeleteCallHandler =
    new ApiCallHandler<HospitalAvailability | null>();

  init() {
    this.hospitalAvailabilityListCallHandler.init([]);
    this.hospitalAvailabilityListByHospitalCallHandler.init([]);

    this.hospitalAvailabilityFetchCallHandler.init();
    this.hospitalAvailabilityPrivateFetchCallHandler.init([]);
    this.hospitalAvailabilitySaveCallHandler.init(null);
    this.hospitalAvailabilityUpdateCallHandler.init(null);
    this.hospitalAvailabilityDeleteCallHandler.init(null);
    this.searchCh.init();
  }

  getAllHospitalAvailabilities() {
    const param: IHttpParams = this.coreService.initCall(
      `/v1/HospitalAvailabilities/list`,
      this.hospitalAvailabilityListCallHandler
    );

    return this.http.get<HospitalAvailability[]>(param);
  }

  getHospitalAvailabilitiesByHospital(hospitalId: string) {
    const param: IHttpParams = this.coreService.initCall(
      `/v1/HospitalAvailabilities/list/${hospitalId}`,
      this.hospitalAvailabilityListByHospitalCallHandler
    );

    return this.http.get<HospitalAvailability[]>(param);
  }

  getHospitalAvailabilitiesByHospitalAndClinic(
    hospitalId: string,
    clinicID: string
  ) {
    const param: IHttpParams = this.coreService.initCall(
      `/v1/HospitalAvailabilities/list/${hospitalId}/${clinicID}`,
      this.hospitalAvailabilityListByHospitalCallHandler
    );

    return this.http.get<HospitalAvailability[]>(param);
  }

  getAllHospitalAvailabilitiesByHospital(hospitalId: string) {
    const param: IHttpParams = this.coreService.initCall(
      `/v1/HospitalAvailabilities/listAll/${hospitalId}`,
      this.hospitalAvailabilityListByHospitalCallHandler
    );

    return this.http.get<HospitalAvailability[]>(param);
  }

  getAllHospitalAvailabilitiesByHospitalAndClinic(
    hospitalId: string,
    clinicID: string
  ) {
    const param: IHttpParams = this.coreService.initCall(
      `/v1/HospitalAvailabilities/listAll/${hospitalId}/${clinicID}`,
      this.hospitalAvailabilityListByHospitalCallHandler
    );

    return this.http.get<HospitalAvailability[]>(param);
  }

  getHospitalAvailabilityById(hospitalAvailabilityId: string) {
    const param: IHttpParams = this.coreService.initCall(
      `/v1/HospitalAvailabilities/fetch/${hospitalAvailabilityId}`,
      this.hospitalAvailabilityFetchCallHandler
    );

    return this.http.get<HospitalAvailability>(param);
  }

  getHospitalAvailabilityByHospitalAndDate(
    hospitalId: string,
    month: number,
    year: number
  ) {
    const param: IHttpParams = this.coreService.initCall(
      `/v1/HospitalAvailabilities/fetch/${hospitalId}/${month}/${year}`,
      this.hospitalAvailabilityFetchCallHandler
    );

    return this.http.get<HospitalAvailability>(param);
  }

  getPrivateHospitalAvailabilityByClinicsAndDate(phab: {
    clinicIds: string[];
    year: number;
    month: number;
  }) {
    const param: IHttpParams = this.coreService.initCall(
      `/v1/HospitalAvailabilities/private/availability`,
      this.hospitalAvailabilityPrivateFetchCallHandler,
      phab
    );

    return this.http.post<PrivateHospitalAvailability[]>(param);
  }

  searchHospitalAvailabilities(
    searchParams: {
      HospitalID?: string | null;
      ClinicID?: string | null;
      SortParams?: ISortParams | null;
    } = { HospitalID: null, ClinicID: null, SortParams: null },
    pagesize: number,
    page: number
  ): Observable<PaginatedSearchResult<HospitalAvailability>> {
    const param: IHttpParams = this.coreService.initCall(
      `/v1/HospitalAvailabilities/search/${pagesize}/${page}`,
      this.searchCh,
      searchParams
    );

    return this.http.post<PaginatedSearchResult<HospitalAvailability>>(param);
  }

  saveHospitalAvailability(hospitalAvailability: HospitalAvailability) {
    const param: IHttpParams = this.coreService.initCall(
      '/v1/HospitalAvailabilities/save',
      this.hospitalAvailabilitySaveCallHandler,
      hospitalAvailability
    );
    return this.http.post(param);
  }

  updateHospitalAvailability(hospitalAvailability: HospitalAvailability) {
    const param: IHttpParams = this.coreService.initCall(
      '/v1/HospitalAvailabilities/update',
      this.hospitalAvailabilityUpdateCallHandler,
      hospitalAvailability
    );
    return this.http.put(param);
  }

  deleteHospitalAvailability(hospitalAvailabilityId: string) {
    const param: IHttpParams = this.coreService.initCall(
      `/v1/HospitalAvailabilities/delete/${hospitalAvailabilityId}`,
      this.hospitalAvailabilityDeleteCallHandler
    );
    return this.http.delete(param);
  }
}
